import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import client from "../api/client";
import { useFinancialYear } from "../context/FinancialYearContext";

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  info: {
    fontSize: 11,
    padding: "7px 10px",
    borderRadius: 10,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
  },
  tableWrap: {
    maxHeight: 430,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 900, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: {
    fontSize: 10,
    padding: "4px 7px",
    borderRadius: 20,
    fontWeight: 600,
  },
};

export default function LedgerPage() {
  const { financialYear } = useFinancialYear();
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState(false);

  const [filters, setFilters] = useState({
    accountId: searchParams.get("accountId") || "",
    fromDate: searchParams.get("fromDate") || financialYear.fromDate,
    toDate: searchParams.get("toDate") || financialYear.toDate,
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    const accountId = searchParams.get("accountId");
    if (!accountId) return;
    setFilters((prev) => ({
      ...prev,
      accountId,
      fromDate: searchParams.get("fromDate") || prev.fromDate,
      toDate: searchParams.get("toDate") || prev.toDate,
    }));
  }, [searchParams]);

  useEffect(() => {
    if (filters.accountId) loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.accountId]);

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const res = await client.get("/accounts");
      setAccounts(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Failed to load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadLedger = async () => {
    if (!filters.accountId) return alert("Please select an account");

    try {
      setLoadingLedger(true);

      const params = new URLSearchParams();
      params.append("accountId", filters.accountId);
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);

      const res = await client.get(`/finance/ledger?${params.toString()}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Failed to load ledger");
    } finally {
      setLoadingLedger(false);
    }
  };

  const formatAmount = (value) => Number(value || 0).toFixed(2);

  const formatBalance = (value) => {
    const amount = Number(value || 0);
    if (amount > 0) return `${amount.toFixed(2)} Dr`;
    if (amount < 0) return `${Math.abs(amount).toFixed(2)} Cr`;
    return "0.00";
  };

  const selectedAccount = accounts.find(
    (a) => String(a.Id ?? a.id) === String(filters.accountId),
  );

  const totalDebit = rows.reduce(
    (sum, row) => sum + Number(row.Debit || row.debit || 0),
    0,
  );

  const totalCredit = rows.reduce(
    (sum, row) => sum + Number(row.Credit || row.credit || 0),
    0,
  );

  const closingBalance =
    rows.length > 0
      ? formatBalance(
          rows[rows.length - 1].RunningBalance ??
            rows[rows.length - 1].runningBalance,
        )
      : "0.00";

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-book me-2"></i>
            Ledger
          </h6>
          <div style={styles.sub}>
            Account-wise transaction ledger with running balance
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 align-items-end mb-2">
            <div className="col-md-5">
              <label style={styles.label}>Account</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filters.accountId}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    accountId: e.target.value,
                  }))
                }
              >
                <option value="">Select Account</option>
                {accounts.map((acc) => (
                  <option key={acc.Id ?? acc.id} value={acc.Id ?? acc.id}>
                    {(acc.AccountCode ?? acc.accountCode) +
                      " - " +
                      (acc.AccountName ?? acc.accountName)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label style={styles.label}>From Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filters.fromDate}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    fromDate: e.target.value,
                  }))
                }
              />
            </div>

            <div className="col-md-2">
              <label style={styles.label}>To Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filters.toDate}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    toDate: e.target.value,
                  }))
                }
              />
            </div>

            <div className="col-md-2 d-grid">
              <button
                onClick={loadLedger}
                className="btn btn-success btn-sm"
                style={styles.btn}
                type="button"
                disabled={loadingLedger}
              >
                <i className="bi bi-search me-1"></i>
                {loadingLedger ? "Loading..." : "Load"}
              </button>
            </div>
          </div>

          <div className="mb-2" style={styles.info}>
            <strong>Account:</strong>{" "}
            {selectedAccount
              ? `${selectedAccount.AccountCode ?? selectedAccount.accountCode} - ${
                  selectedAccount.AccountName ?? selectedAccount.accountName
                }`
              : "-"}
          </div>

          {loadingAccounts ? (
            <div className="text-muted" style={{ fontSize: 11 }}>
              Loading accounts...
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table
                className="table table-bordered table-hover align-middle"
                style={styles.table}
              >
                <thead>
                  <tr>
                    <th style={{ ...styles.th, minWidth: 110 }}>Date</th>
                    <th style={{ ...styles.th, minWidth: 130 }}>Voucher No</th>
                    <th style={{ ...styles.th, minWidth: 280 }}>Description</th>
                    <th
                      className="text-end"
                      style={{ ...styles.th, minWidth: 120 }}
                    >
                      Debit
                    </th>
                    <th
                      className="text-end"
                      style={{ ...styles.th, minWidth: 120 }}
                    >
                      Credit
                    </th>
                    <th
                      className="text-end"
                      style={{ ...styles.th, minWidth: 130 }}
                    >
                      Balance
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="text-center text-muted"
                        style={{ ...styles.td, padding: 18 }}
                      >
                        No ledger entries found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => {
                      const balance = Number(
                        row.RunningBalance ?? row.runningBalance ?? 0,
                      );

                      return (
                        <tr key={index}>
                          <td style={styles.td}>
                            {String(
                              row.EntryDate ?? row.entryDate ?? "",
                            ).substring(0, 10)}
                          </td>

                          <td style={{ ...styles.td, fontWeight: 700 }}>
                            {row.VoucherNo ?? row.voucherNo}
                          </td>

                          <td style={styles.td}>
                            {row.Description ?? row.description}
                          </td>

                          <td className="text-end" style={styles.td}>
                            {formatAmount(row.Debit ?? row.debit)}
                          </td>

                          <td className="text-end" style={styles.td}>
                            {formatAmount(row.Credit ?? row.credit)}
                          </td>

                          <td className="text-end" style={styles.td}>
                            <span
                              className={`badge ${
                                balance >= 0
                                  ? "text-bg-success"
                                  : "text-bg-danger"
                              }`}
                              style={styles.badge}
                            >
                              {formatBalance(balance)}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>

                <tfoot>
                  <tr>
                    <td
                      colSpan="3"
                      style={{
                        ...styles.td,
                        fontWeight: 700,
                        background: "#f8f9fa",
                      }}
                    >
                      Totals
                    </td>
                    <td
                      className="text-end"
                      style={{ ...styles.td, fontWeight: 700 }}
                    >
                      {totalDebit.toFixed(2)}
                    </td>
                    <td
                      className="text-end"
                      style={{ ...styles.td, fontWeight: 700 }}
                    >
                      {totalCredit.toFixed(2)}
                    </td>
                    <td
                      className="text-end"
                      style={{ ...styles.td, fontWeight: 700 }}
                    >
                      {closingBalance}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
